import { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import apiService from "../../../../../../service/Api";

const DARK_BLUE = "#4a96e2ff";
const LIGHT_BLUE = "#6fabddcc";

// ---------------- Center Circle / Label ----------------
const StyledText = styled("text")(() => ({
  fill: "#ffffff",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 18,
  fontWeight: 600,
}));

function PieCenterLabel({ children }: { children: React.ReactNode }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}

function PieCenterCircle({ radius }: { radius: number }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <circle
      cx={left + width / 2}
      cy={top + height / 2}
      r={radius}
      fill={DARK_BLUE}
    />
  );
}

// ---------------- Types ----------------
interface VenueTypeDistributionItem {
  id: string | number;
  value: number;
  label: string;
  color?: string;
}

interface VenueTypeDistributionResponse {
  data: VenueTypeDistributionItem[];
  total_venues: number;
  title: string;
}

// ---------------- Widget ----------------
const CHART_SIZE = 480; // increased for more spacing

export default function VenueTypesWidget() {
  const [data, setData] = useState<VenueTypeDistributionItem[]>([]);
  const [title, setTitle] = useState<string>("Venues");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await apiService.get<VenueTypeDistributionResponse>(
          "/api/venues/dashboard/venue-type-distribution/"
        );

        if (!res.data?.data?.length) {
          setError("No data available");
          return;
        }

        setTitle(res.data.title || "Venues");

        setData(
          res.data.data.map((item) => ({
            id: item.id,
            value: item.value,
            label: item.label,
            color: item.color || LIGHT_BLUE,
          }))
        );
      } catch (err) {
        console.error("Failed to fetch venue type distribution:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>{error}</Typography>;

  return (
    <Card sx={{ height: "100%", borderRadius: 3, width: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Venue Types
        </Typography>

        <Box
          sx={{
            width: CHART_SIZE,
            height: CHART_SIZE,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
          }}
        >
          <PieChart
            width={CHART_SIZE}
            height={CHART_SIZE}
            series={[
              {
                data,
                innerRadius: CHART_SIZE * 0.18, // smaller inner circle
                outerRadius: CHART_SIZE * 0.45, // bigger outer radius
                paddingAngle: 1, // space between slices
                arcLabel: (item) => `${item.label} (${item.value})`,
                highlightScope: { fade: "global", highlight: "item" },
                faded: {
                  innerRadius: CHART_SIZE * 0.18,
                  additionalRadius: -10,
                },
              },
            ]}
            sx={{
              [`& .${pieArcLabelClasses.root}`]: {
                fontSize: 12,
                fill: "#ffffff",
              },
            }}
            hideLegend
          >
            <PieCenterCircle radius={CHART_SIZE * 0.18} />
            <PieCenterLabel>{title}</PieCenterLabel>
          </PieChart>
        </Box>
      </CardContent>
    </Card>
  );
}
